import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import api from '../lib/api';
import { toast } from 'sonner';
import { useNotificationSound } from '../hooks/useNotificationSound';
import { 
  Volume2, 
  Vibrate, 
  Save, 
  Loader2,
  Play,
  PackageCheck,
  RefreshCw,
  XCircle,
  Upload,
  Trash2,
  Music,
  FileAudio
} from 'lucide-react';

const VIBRATION_PRESETS = [
  { id: 'gentle', name: 'Doux', pattern: [100, 50, 100] },
  { id: 'normal', name: 'Normal', pattern: [200, 100, 200] },
  { id: 'urgent', name: 'Urgent', pattern: [500, 200, 500] },
  { id: 'rapid', name: 'Rapide', pattern: [100, 50, 100, 50, 100] },
  { id: 'none', name: 'Aucune', pattern: [] },
];

function getVibrationPresetId(pattern) {
  if (!pattern || pattern.length === 0) return 'none';
  const preset = VIBRATION_PRESETS.find(p => 
    JSON.stringify(p.pattern) === JSON.stringify(pattern)
  );
  return preset?.id || 'normal';
}

export function NotificationToneSettings() {
  const { testSound, initAudio } = useNotificationSound();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [availableSounds, setAvailableSounds] = useState([]);
  const [uploadName, setUploadName] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const fileInputRef = useRef(null);
  
  const [settings, setSettings] = useState({
    new_order_sound: 'chime',
    reassignment_sound: 'bell',
    cancellation_sound: 'alert',
    new_order_vibration: [200, 100, 200],
    reassignment_vibration: [100, 50, 100, 50, 100],
    cancellation_vibration: [500, 200, 500],
    require_interaction: true,
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [settingsRes, soundsRes] = await Promise.all([
        api.get('/push/tone-settings'),
        api.get('/push/available-sounds')
      ]);
      setSettings(prev => ({ ...prev, ...settingsRes.data }));
      setAvailableSounds(soundsRes.data.sounds || []);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/push/tone-settings', settings);
      toast.success('Paramètres de tonalité enregistrés');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const handleSoundChange = (type, value) => {
    setSettings(prev => ({ ...prev, [`${type}_sound`]: value }));
  };

  const handleVibrationChange = (type, presetId) => {
    const preset = VIBRATION_PRESETS.find(p => p.id === presetId);
    if (preset) {
      setSettings(prev => ({ ...prev, [`${type}_vibration`]: preset.pattern }));
    }
  };

  const handleTestSound = (soundId) => {
    initAudio();
    const sound = availableSounds.find(s => s.id === soundId);
    if (sound?.type === 'custom' && sound?.url) {
      testSound(soundId, sound.url);
    } else {
      testSound(soundId);
    }
  };

  const handleTestVibration = (pattern) => {
    if ('vibrate' in navigator && pattern.length > 0) {
      navigator.vibrate(pattern);
    } else {
      toast.info('Vibration non disponible sur cet appareil');
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.name.match(/\.(mp3|wav|ogg)$/i)) {
        toast.error('Format non supporté. Utilisez MP3, WAV ou OGG.');
        return;
      }
      if (file.size > 1024 * 1024) {
        toast.error('Fichier trop volumineux (max 1 Mo)');
        return;
      }
      setUploadFile(file);
      if (!uploadName) {
        setUploadName(file.name.replace(/\.[^/.]+$/, ''));
      }
    }
  };

  const handleUpload = async () => {
    if (!uploadFile || !uploadName.trim()) {
      toast.error('Veuillez sélectionner un fichier et donner un nom');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('name', uploadName.trim());
      formData.append('notification_type', 'all');

      const response = await api.post('/push/upload-sound', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        toast.success('Son uploadé avec succès!');
        setUploadDialogOpen(false);
        setUploadFile(null);
        setUploadName('');
        // Reload available sounds
        const soundsRes = await api.get('/push/available-sounds');
        setAvailableSounds(soundsRes.data.sounds || []);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'upload');
      console.error(error);
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteSound = async (soundId) => {
    if (!confirm('Supprimer ce son personnalisé ?')) return;
    
    try {
      await api.delete(`/push/sounds/${soundId}`);
      toast.success('Son supprimé');
      // Reload available sounds
      const soundsRes = await api.get('/push/available-sounds');
      setAvailableSounds(soundsRes.data.sounds || []);
    } catch (error) {
      toast.error('Erreur lors de la suppression');
      console.error(error);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  const notificationTypes = [
    { 
      id: 'new_order', 
      label: 'Nouvelle commande', 
      icon: PackageCheck,
      color: 'text-green-500',
      bgColor: 'bg-green-50'
    },
    { 
      id: 'reassignment', 
      label: 'Réassignation', 
      icon: RefreshCw,
      color: 'text-blue-500',
      bgColor: 'bg-blue-50'
    },
    { 
      id: 'cancellation', 
      label: 'Annulation', 
      icon: XCircle,
      color: 'text-red-500',
      bgColor: 'bg-red-50'
    },
  ];

  const defaultSounds = availableSounds.filter(s => s.type === 'default');
  const customSounds = availableSounds.filter(s => s.type === 'custom');

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <Volume2 className="w-5 h-5 text-violet-500" />
              Tonalités des notifications
            </CardTitle>
            <CardDescription>
              Configurer les sons et vibrations pour chaque type de notification
            </CardDescription>
          </div>
          <Badge variant="outline" className="bg-violet-50 text-violet-700 border-violet-200">
            Admin
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Upload custom sound button */}
        <div className="flex items-center justify-between p-3 rounded-lg border border-dashed border-violet-300 bg-violet-50/50">
          <div className="flex items-center gap-2">
            <FileAudio className="w-5 h-5 text-violet-500" />
            <div>
              <p className="text-sm font-medium">Sons personnalisés</p>
              <p className="text-xs text-muted-foreground">
                {customSounds.length} son(s) uploadé(s)
              </p>
            </div>
          </div>
          <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm" className="gap-1">
                <Upload className="w-4 h-4" />
                Ajouter un son
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Music className="w-5 h-5 text-violet-500" />
                  Uploader un son personnalisé
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-4">
                <div className="space-y-2">
                  <Label>Nom du son</Label>
                  <Input
                    placeholder="Ex: Ma sonnerie"
                    value={uploadName}
                    onChange={(e) => setUploadName(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Fichier audio (MP3, WAV, OGG - max 1 Mo)</Label>
                  <div 
                    className="border-2 border-dashed rounded-lg p-6 text-center cursor-pointer hover:border-violet-400 transition-colors"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".mp3,.wav,.ogg"
                      className="hidden"
                      onChange={handleFileSelect}
                    />
                    {uploadFile ? (
                      <div className="flex items-center justify-center gap-2 text-violet-600">
                        <FileAudio className="w-5 h-5" />
                        <span className="font-medium">{uploadFile.name}</span>
                      </div>
                    ) : (
                      <div className="text-muted-foreground">
                        <Upload className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        <p className="text-sm">Cliquez pour sélectionner un fichier</p>
                      </div>
                    )}
                  </div>
                </div>
                <Button 
                  onClick={handleUpload} 
                  disabled={uploading || !uploadFile || !uploadName.trim()}
                  className="w-full bg-violet-600 hover:bg-violet-700"
                >
                  {uploading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Upload en cours...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Uploader
                    </>
                  )}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* List of custom sounds */}
        {customSounds.length > 0 && (
          <div className="space-y-2">
            <Label className="text-sm text-muted-foreground">Vos sons personnalisés</Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {customSounds.map((sound) => (
                <div 
                  key={sound.id} 
                  className="flex items-center justify-between p-2 rounded-lg bg-slate-50 border"
                >
                  <div className="flex items-center gap-2">
                    <Music className="w-4 h-4 text-violet-500" />
                    <span className="text-sm font-medium truncate">{sound.name}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => handleTestSound(sound.id)}
                    >
                      <Play className="w-3 h-3" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-red-500 hover:text-red-600"
                      onClick={() => handleDeleteSound(sound.id)}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Notification types configuration */}
        {notificationTypes.map((type) => (
          <div key={type.id} className={`p-4 rounded-lg border ${type.bgColor}`}>
            <div className="flex items-center gap-2 mb-4">
              <type.icon className={`w-5 h-5 ${type.color}`} />
              <span className="font-medium">{type.label}</span>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Sound selection */}
              <div className="space-y-2">
                <Label className="text-sm text-muted-foreground flex items-center gap-1">
                  <Volume2 className="w-3 h-3" /> Son
                </Label>
                <div className="flex gap-2">
                  <Select
                    value={settings[`${type.id}_sound`]}
                    onValueChange={(v) => handleSoundChange(type.id, v)}
                  >
                    <SelectTrigger className="flex-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <div className="px-2 py-1 text-xs text-muted-foreground font-medium">Par défaut</div>
                      {defaultSounds.map((sound) => (
                        <SelectItem key={sound.id} value={sound.id}>
                          {sound.id === 'chime' && '🎵'} 
                          {sound.id === 'bell' && '🔔'} 
                          {sound.id === 'alert' && '🚨'} 
                          {sound.id === 'ping' && '📍'} 
                          {sound.id === 'none' && '🔇'} 
                          {' '}{sound.name}
                        </SelectItem>
                      ))}
                      {customSounds.length > 0 && (
                        <>
                          <div className="px-2 py-1 text-xs text-muted-foreground font-medium border-t mt-1 pt-2">Personnalisés</div>
                          {customSounds.map((sound) => (
                            <SelectItem key={sound.id} value={sound.id}>
                              🎶 {sound.name}
                            </SelectItem>
                          ))}
                        </>
                      )}
                    </SelectContent>
                  </Select>
                  <Button
                    variant="outline"
                    size="icon"
                    className="bg-white hover:bg-green-50 hover:text-green-600 hover:border-green-300"
                    onClick={() => {
                      initAudio();
                      handleTestSound(settings[`${type.id}_sound`]);
                      toast.info('🔊 Test du son...');
                    }}
                    disabled={settings[`${type.id}_sound`] === 'none'}
                    title="Tester le son"
                  >
                    <Play className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Vibration selection */}
              <div className="space-y-2">
                <Label className="text-sm text-muted-foreground flex items-center gap-1">
                  <Vibrate className="w-3 h-3" /> Vibration
                </Label>
                <div className="flex gap-2">
                  <Select
                    value={getVibrationPresetId(settings[`${type.id}_vibration`])}
                    onValueChange={(v) => handleVibrationChange(type.id, v)}
                  >
                    <SelectTrigger className="flex-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {VIBRATION_PRESETS.map((preset) => (
                        <SelectItem key={preset.id} value={preset.id}>
                          {preset.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => handleTestVibration(settings[`${type.id}_vibration`])}
                    disabled={!settings[`${type.id}_vibration`]?.length}
                  >
                    <Vibrate className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        ))}

        {/* Global settings */}
        <div className="pt-4 border-t space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <Label>Notifications persistantes</Label>
              <p className="text-sm text-muted-foreground">
                Les notifications restent affichées jusqu'à ce qu'on les ferme
              </p>
            </div>
            <Switch
              checked={settings.require_interaction}
              onCheckedChange={(v) => setSettings(prev => ({ ...prev, require_interaction: v }))}
            />
          </div>
        </div>

        {/* Save button */}
        <Button 
          onClick={handleSave} 
          disabled={saving}
          className="w-full bg-violet-600 hover:bg-violet-700"
        >
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Enregistrement...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Enregistrer les paramètres
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  );
}

export default NotificationToneSettings;
